// tile-map.js — main-thread OpenStreetMap vector-tile renderer (no web workers).
// Fetches OpenFreeMap vector tiles, decodes them on the main thread, and paints
// them to a 2D canvas recolored to a theme palette. Exposes window.TileMap.
(function () {
  let LIBS = null, TJ = null;
  // Aerial photo mode — Esri World Imagery raster tiles (z/y/x). CORS-enabled,
  // so poster capture / print export keep working (canvas stays untainted).
  const SAT_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
  const SAT_MAXZ = 18;
  async function libs() {
    if (LIBS) return LIBS;
    const [vt, pbf] = await Promise.all([
      import("https://esm.sh/@mapbox/vector-tile@1.3.1"),
      import("https://esm.sh/pbf@3.2.1"),
    ]);
    LIBS = { VectorTile: vt.VectorTile, Pbf: pbf.default };
    return LIBS;
  }
  async function tilejson() {
    if (TJ) return TJ;
    const j = await (await fetch("https://tiles.openfreemap.org/planet")).json();
    TJ = { tiles: j.tiles, maxzoom: j.maxzoom || 14, minzoom: j.minzoom || 0 };
    return TJ;
  }

  const lon2x = (lon, ws) => (lon + 180) / 360 * ws;
  const lat2y = (lat, ws) => { const r = lat * Math.PI / 180; return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * ws; };
  const x2lon = (x, ws) => x / ws * 360 - 180;
  const y2lat = (y, ws) => { const n = Math.PI * (1 - 2 * y / ws); return 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))); };
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  function hx(h) { h = h.replace("#", ""); if (h.length === 3) h = h.split("").map((c) => c + c).join(""); return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; }
  function mix(a, b, w) { const pa = hx(a), pb = hx(b); return "rgb(" + Math.round(pa[0] + (pb[0] - pa[0]) * w) + "," + Math.round(pa[1] + (pb[1] - pa[1]) * w) + "," + Math.round(pa[2] + (pb[2] - pa[2]) * w) + ")"; }

  const MINOR = { minor: 1, service: 1, track: 1, path: 1, pedestrian: 1, living_street: 1, footway: 1, cycleway: 1 };
  const MAJOR = { primary: 1, secondary: 1, tertiary: 1 };
  const HWY = { motorway: 1, trunk: 1 };

  class TileMap {
    constructor(opts) {
      this.canvas = opts.canvas;
      this.ctx = this.canvas.getContext("2d");
      this.theme = opts.theme;
      this.detail = opts.detail || "rich";
      this.markerStyle = opts.markerStyle || "pin";
      this.view = { lng: opts.lng, lat: opts.lat, zoom: opts.zoom };
      this.pin = { lng: opts.lng, lat: opts.lat };
      this.onReady = opts.onReady || function () {};
      this.onView = opts.onView || function () {};
      this.dpr = Math.min(1.5, window.devicePixelRatio || 1);
      this.cache = new Map();
      this.pending = new Set();
      this.idleCbs = [];
      this.ready = false;
      this._raf = 0;
      this._init();
    }
    async _init() {
      await libs();
      this.tj = await tilejson();
      this.ready = true;
      this.resize();
      this.onReady();
      this.onView(this.view);
    }
    setTheme(t, d) { this.theme = t; this.detail = d; this.schedule(); }
    setView(v) { this.view = Object.assign({}, this.view, v); this.schedule(); this.onView(this.view); }
    setPin(lng, lat) { this.pin = { lng, lat }; this.schedule(); }
    setMarker(m) { this.markerStyle = m; this.schedule(); }

    // project a lng/lat to canvas pixels (backing px) for a given view + scale
    _proj(lng, lat, W, H, view, scale) {
      const tz = clamp(Math.round(view.zoom), this.tj.minzoom, this.tj.maxzoom);
      const wsTile = 256 * Math.pow(2, tz);
      const s = Math.pow(2, view.zoom - tz) * scale;
      const cWX = lon2x(view.lng, wsTile), cWY = lat2y(view.lat, wsTile);
      return { x: W / 2 + (lon2x(lng, wsTile) - cWX) * s, y: H / 2 + (lat2y(lat, wsTile) - cWY) * s };
    }
    // convert a CSS-pixel point on the live canvas to lng/lat
    screenToLngLat(cssX, cssY) {
      if (!this.tj) return null;
      const tz = clamp(Math.round(this.view.zoom), this.tj.minzoom, this.tj.maxzoom);
      const wsTile = 256 * Math.pow(2, tz);
      const s = Math.pow(2, this.view.zoom - tz) * this.dpr;
      const cWX = lon2x(this.view.lng, wsTile), cWY = lat2y(this.view.lat, wsTile);
      const wx = cWX + (cssX * this.dpr - this.canvas.width / 2) / s;
      const wy = cWY + (cssY * this.dpr - this.canvas.height / 2) / s;
      return { lng: x2lon(wx, wsTile), lat: y2lat(clamp(wy, 1, wsTile - 1), wsTile) };
    }
    // is a CSS-pixel point on/near the marker? (for grab-to-move)
    hitPin(cssX, cssY) {
      if (!this.tj || !this.markerStyle || this.markerStyle === "none") return false;
      const p = this._proj(this.pin.lng, this.pin.lat, this.canvas.width, this.canvas.height, this.view, this.dpr);
      const ax = p.x / this.dpr, ay = p.y / this.dpr;            // anchor in CSS px
      const fcss = Math.min(this.canvas.width, this.canvas.height) / this.dpr * 0.0010;
      // pin anchors at its tip (body above); others are centred on the anchor
      const cx = ax, cy = this.markerStyle === "pin" ? ay - 30 * fcss : ay;
      const r = (this.markerStyle === "pin" ? 46 : 26) * fcss;
      return (cssX - cx) * (cssX - cx) + (cssY - cy) * (cssY - cy) <= r * r;
    }
    // While dragging/zooming we draw fewer layers (skip buildings + minor
    // streets) so interaction stays smooth; full detail repaints on release.
    setDragging(on) { const was = this._drag; this._drag = !!on; if (was && !on) { this.ensurePinVisible(); this.schedule(); } }
    // If a map pan/zoom pushed the marker off (or nearly off) the visible map,
    // snap it back to the centre so it never gets lost.
    ensurePinVisible() {
      if (!this.tj || !this.markerStyle || this.markerStyle === "none" || !this.pin) return false;
      const W = this.canvas.width, H = this.canvas.height;
      const p = this._proj(this.pin.lng, this.pin.lat, W, H, this.view, this.dpr);
      const f = Math.min(W, H) * 0.0010;
      const top = this.markerStyle === "pin" ? 74 * f : 28 * f;   // pin body rises above its anchor
      const bot = this.markerStyle === "pin" ? 8 * f : 28 * f;
      const side = 28 * f;
      if (p.x < side || p.x > W - side || p.y < top || p.y > H - bot) {
        this.pin = { lng: this.view.lng, lat: this.view.lat };
        this.schedule();
        return true;
      }
      return false;
    }
    resize() {
      const r = this.canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      this.canvas.width = Math.round(r.width * this.dpr);
      this.canvas.height = Math.round(r.height * this.dpr);
      this.render();
    }
    schedule() { if (this._raf) return; this._raf = requestAnimationFrame(() => { this._raf = 0; this.render(); }); }
    key(z, x, y, sat) { return (sat ? "s" : "v") + z + "/" + x + "/" + y; }

    async ensure(z, x, y, sat) {
      const k = this.key(z, x, y, sat);
      if (this.cache.has(k)) return;
      this.cache.set(k, "loading");
      this.pending.add(k);
      try {
        if (sat) {
          const img = await new Promise((res, rej) => {
            const im = new Image();
            im.crossOrigin = "anonymous";
            im.onload = () => res(im);
            im.onerror = () => rej(new Error("tile failed"));
            im.src = SAT_URL.replace("{z}", z).replace("{y}", y).replace("{x}", x);
          });
          this.cache.set(k, { img });
        } else {
          const url = this.tj.tiles[0].replace("{z}", z).replace("{x}", x).replace("{y}", y);
          const buf = await (await fetch(url)).arrayBuffer();
          const vt = new LIBS.VectorTile(new LIBS.Pbf(buf));
          this.cache.set(k, this._prepare(vt));   // decode geometry ONCE, not per frame
        }
      } catch (e) { this.cache.set(k, "error"); }
      this.pending.delete(k);
      this.schedule();
      if (this.pending.size === 0) { const cbs = this.idleCbs; this.idleCbs = []; cbs.forEach((f) => f()); }
    }
    idle() { return new Promise((res) => { if (this.ready && this.pending.size === 0) res(); else this.idleCbs.push(res); }); }

    // render to the live canvas, or to a provided target for export
    render(exp) {
      const ctx = exp ? exp.ctx : this.ctx;
      const W = exp ? exp.w : this.canvas.width;
      const H = exp ? exp.h : this.canvas.height;
      const scale = exp ? exp.scale : this.dpr;
      const view = exp && exp.view ? exp.view : this.view;
      const theme = this.theme, detail = this.detail;
      const sat = !!(theme && theme.satellite);
      ctx.fillStyle = (sat && theme.mapBg) || theme.bg; ctx.fillRect(0, 0, W, H);
      if (!this.ready) return;

      const maxz = sat ? SAT_MAXZ : this.tj.maxzoom;
      let tz = clamp(Math.round(view.zoom), this.tj.minzoom, maxz);
      // Print/export renders at a large scale factor — pull higher-zoom imagery
      // so the upscaled aerial photo stays sharp instead of going soft.
      if (sat && exp && scale > 1.4) tz = clamp(tz + Math.min(3, Math.round(Math.log2(scale))), this.tj.minzoom, maxz);
      const wsTile = 256 * Math.pow(2, tz);
      const s = Math.pow(2, view.zoom - tz) * scale;
      const cWX = lon2x(view.lng, wsTile), cWY = lat2y(view.lat, wsTile);
      const cx = W / 2, cy = H / 2;
      const N = Math.pow(2, tz);

      const halfWx = (W / 2) / s, halfHy = (H / 2) / s;
      const minTX = Math.floor((cWX - halfWx) / 256), maxTX = Math.floor((cWX + halfWx) / 256);
      const minTY = Math.floor((cWY - halfHy) / 256), maxTY = Math.floor((cWY + halfHy) / 256);

      for (let tx = minTX; tx <= maxTX; tx++) {
        for (let ty = minTY; ty <= maxTY; ty++) {
          if (ty < 0 || ty >= N) continue;
          const wx = ((tx % N) + N) % N;
          const k = this.key(tz, wx, ty, sat);
          const tile = this.cache.get(k);
          if (!tile) { this.ensure(tz, wx, ty, sat); continue; }   // request missing tiles in export framing too, not just the live view
          if (tile === "loading" || tile === "error") continue;
          const ox = tx * 256, oy = ty * 256;
          if (sat) {
            // slight overdraw hides hairline seams between photo tiles
            const px0 = (ox - cWX) * s + cx, py0 = (oy - cWY) * s + cy;
            if (theme.photoFilter) ctx.filter = theme.photoFilter;
            ctx.drawImage(tile.img, px0, py0, 256 * s + 0.55, 256 * s + 0.55);
            if (theme.photoFilter) ctx.filter = "none";
          } else {
            this._tile(ctx, tile, ox, oy, cWX, cWY, s, cx, cy, theme, detail, tz, exp);
          }
        }
      }

      // marker — anchored to its lng/lat so it stays on the chosen spot while
      // the map pans; the customer can also grab it and drag it anywhere.
      if (this.markerStyle && this.markerStyle !== "none" && window.drawMarker) {
        const pin = this.pin || { lng: view.lng, lat: view.lat };
        const pp = this._proj(pin.lng, pin.lat, W, H, view, scale);
        const f = Math.min(W, H) * 0.0010;
        ctx.save(); ctx.translate(pp.x, pp.y); ctx.scale(f, f);
        window.drawMarker(ctx, this.markerStyle, theme, 0, 0); ctx.restore();
      }
    }

    // Decode a raw vector tile ONCE into flat Float32Array rings in 256-unit
    // tile space. Per-frame rendering then only multiply-adds these into screen
    // space — no protobuf geometry decoding or per-point division while panning.
    _prepare(tile) {
      const P = { land: [], water: [], lw: [], lmin: [], lmaj: [], lhwy: [] };
      const grab = (name, out, filter) => {
        const l = tile.layers[name]; if (!l) return; const k = 256 / l.extent;
        for (let i = 0; i < l.length; i++) {
          const f = l.feature(i); if (filter && !filter(f.properties)) continue;
          const g = f.loadGeometry();
          for (const ring of g) {
            const a = new Float32Array(ring.length * 2);
            for (let j = 0; j < ring.length; j++) { a[j * 2] = ring[j].x * k; a[j * 2 + 1] = ring[j].y * k; }
            out.push(a);
          }
        }
      };
      grab("landcover", P.land); grab("park", P.land); grab("water", P.water);
      grab("waterway", P.lw);
      grab("transportation", P.lmin, (p) => MINOR[p.class]);
      grab("transportation", P.lmaj, (p) => MAJOR[p.class]);
      grab("transportation", P.lhwy, (p) => HWY[p.class]);
      return P;
    }

    _tile(ctx, P, ox, oy, cWX, cWY, s, cx, cy, theme, detail, tz, exp) {
      const fast = this._drag && !exp;   // lighter detail only on the live canvas while dragging
      const path = (rings, close) => {
        ctx.beginPath();
        for (const a of rings) {
          for (let i = 0; i < a.length; i += 2) {
            const px = (ox + a[i] - cWX) * s + cx, py = (oy + a[i + 1] - cWY) * s + cy;
            i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
          }
          if (close) ctx.closePath();
        }
      };
      const fill = (rings, color) => { if (!rings.length) return; path(rings, true); ctx.fillStyle = color; ctx.fill(); };
      const stroke = (rings, color, width) => { if (!rings.length || width <= 0) return; path(rings, false); ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.stroke(); };

      fill(P.land, theme.park);
      fill(P.water, theme.water);
      stroke(P.lw, theme.water, Math.max(0.6, 1.1 * s));
      if (!fast && detail !== "minimal") stroke(P.lmin, theme.minor, Math.max(0.35, 0.8 * s));
      stroke(P.lmaj, theme.major, Math.max(0.7, 1.5 * s));
      stroke(P.lhwy, theme.highway, Math.max(1.1, 2.6 * s));
    }
  }

  window.TileMap = TileMap;
  window.TileMapHelpers = { lon2x, lat2y, x2lon, y2lat, clamp };
})();
